import {
	type ComponentProps,
	Fragment,
	type ReactNode,
	createContext,
	useContext,
} from "react";
import { Card } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { ItemTypesCard } from "../SkillProgressCards/ItemTypesCard";
import { TipCallout } from "../TipCallout";
import { ExamActionCard } from "./ExamActionCard";
import { ExamActionCardSkeleton } from "./ExamActionCardSkeleton";
import { ExamInfoCard } from "./ExamInfoCard";
import { ItemTypesSectionSkeleton } from "./ItemTypesSectionSkeleton";
import { ProductsSection } from "./ProductsSection";
import { SectionHeader } from "./SectionHeader";
import { TasksSection } from "./TasksSection";
import { TopicsSection } from "./TopicsSection";
import type {
	ItemTypesSectionItem,
	ProductsSectionItem,
	Section,
	TasksSectionItem,
	TopicsSectionItem,
} from "./types";

export type HomeAppletValue = {
	header: {
		examInfo: ComponentProps<typeof ExamInfoCard>;
		examAction?: ComponentProps<typeof ExamActionCard>;
		examActionLoading?: boolean;
		tip?: {
			text?: string;
			isLoading?: boolean;
		};
	};
	sections: Section[];
	options: {
		appFont: string;
		showStudyPackViews?: boolean;
		studyPackSubtitle?: string;
		onNavigate: (link: string) => void;
		onSelectTopic: (id: string) => void;
		onShowPurchase: () => void;
		onViewItemTypeDetails: (questionTypeId: string) => void;
	};
};

type HomeAppletProps = {
	children: ReactNode;
	value: HomeAppletValue;
};

const HomeAppletContext = createContext<HomeAppletValue | null>(null);

const useHomeApplet = () => {
	const value = useContext(HomeAppletContext);
	if (!value) {
		throw new Error(
			"HomeApplet compound components must be rendered inside HomeApplet.",
		);
	}
	return value;
};

const HomeAppletProvider = ({ children, value }: HomeAppletProps) => (
	<HomeAppletContext.Provider value={value}>
		{children}
	</HomeAppletContext.Provider>
);

type HeaderVariantProps = {
	header: HomeAppletValue["header"];
	isLoading: boolean;
	actionLoading: boolean;
};

type HomeSummaryTip = NonNullable<HomeAppletValue["header"]["tip"]>;

const shouldShowTip = (tip?: HomeSummaryTip) =>
	Boolean(tip?.isLoading || tip?.text?.trim());

const HomeSummaryTipCard = ({
	tip,
	style,
}: {
	tip?: HomeSummaryTip;
	style?: ComponentProps<typeof FlatCard>["style"];
}) => (
	<FlatCard style={style}>
		<Card.Content style={{ padding: 16 }}>
			<TipCallout text={tip?.text} isLoading={tip?.isLoading} variant="flat" />
		</Card.Content>
	</FlatCard>
);

const MobileHomeAppletHeader = ({ header, isLoading }: HeaderVariantProps) => {
	const showTip = shouldShowTip(header.tip);
	return (
		<Box>
			<ExamInfoCard
				{...header.examInfo}
				isLoading={isLoading}
				footer={
					showTip ? (
						<TipCallout
							text={header.tip?.text}
							isLoading={header.tip?.isLoading}
							variant="flat"
						/>
					) : null
				}
			/>
		</Box>
	);
};

const DesktopHomeAppletHeader = ({
	header,
	isLoading,
	actionLoading,
}: HeaderVariantProps) => {
	const theme = useAppTheme();
	const showTip = shouldShowTip(header.tip);
	const actionCardStyle = {
		borderTopLeftRadius: theme.roundness,
		borderTopRightRadius: theme.roundness,
		...(showTip
			? {
					borderBottomLeftRadius: theme.roundness,
					borderBottomRightRadius: theme.roundness,
				}
			: {}),
	};
	return (
		<Box gap={2}>
			<ExamInfoCard
				{...header.examInfo}
				isLoading={isLoading}
				style={
					actionLoading || header.examAction || showTip
						? {
								borderBottomLeftRadius: theme.roundness,
								borderBottomRightRadius: theme.roundness,
							}
						: undefined
				}
			/>
			{actionLoading ? (
				<ExamActionCardSkeleton />
			) : header.examAction ? (
				<ExamActionCard {...header.examAction} style={actionCardStyle} />
			) : null}
			{showTip ? (
				<HomeSummaryTipCard
					tip={header.tip}
					style={{
						borderTopLeftRadius: theme.roundness,
						borderTopRightRadius: theme.roundness,
					}}
				/>
			) : null}
		</Box>
	);
};

const HomeAppletHeader = () => {
	const { header } = useHomeApplet();
	const { isMobile } = useDevice();
	const isLoading = Boolean(header.examInfo.isLoading);
	const actionLoading = Boolean(header.examActionLoading);

	const Variant = isMobile ? MobileHomeAppletHeader : DesktopHomeAppletHeader;
	return (
		<Variant
			header={header}
			isLoading={isLoading}
			actionLoading={actionLoading}
		/>
	);
};

type HomeAppletTasksProps = {
	data: TasksSectionItem[];
	isLoading?: boolean;
};

const HomeAppletTasks = ({ data, isLoading }: HomeAppletTasksProps) => (
	<TasksSection isLoading={isLoading} items={data} />
);

type HomeAppletProductsProps = {
	data: ProductsSectionItem[];
	isLoading?: boolean;
};

const HomeAppletProducts = ({ data, isLoading }: HomeAppletProductsProps) => {
	const { options } = useHomeApplet();
	const { isMobile } = useDevice();
	return (
		<Box paddingHorizontal={isMobile ? 16 : 0}>
			<ProductsSection
				isLoading={isLoading}
				items={data}
				onPressItem={options.onNavigate}
			/>
		</Box>
	);
};

type HomeAppletTopicsProps = {
	data: TopicsSectionItem[];
	isLoading?: boolean;
};

const HomeAppletTopics = ({ data, isLoading }: HomeAppletTopicsProps) => {
	const { options } = useHomeApplet();
	return (
		<TopicsSection
			isLoading={isLoading}
			items={data}
			showStudyPackViews={options.showStudyPackViews}
			studyPackSubtitle={options.studyPackSubtitle}
			onSelectTopic={options.onSelectTopic}
			onShowPurchase={options.onShowPurchase}
		/>
	);
};

type HomeAppletItemTypesProps = {
	data: ItemTypesSectionItem[];
	isLoading?: boolean;
};

const HomeAppletItemTypes = ({
	data,
	isLoading = false,
}: HomeAppletItemTypesProps) => {
	const { options } = useHomeApplet();
	if (isLoading) {
		return <ItemTypesSectionSkeleton />;
	}
	return (
		<Box gap={12}>
			{data.map((item) => (
				<ItemTypesCard
					key={item.id}
					title={item.title}
					subtitle={item.subtitle}
					icon={item.icon}
					rows={item.rows}
					tip={item.tip}
					tipLoading={item.tipLoading}
					onViewDetails={options.onViewItemTypeDetails}
				/>
			))}
		</Box>
	);
};

const HomeAppletSectionItems = ({ section }: { section: Section }) => {
	switch (section.type) {
		case "tasks":
			return (
				<HomeApplet.Tasks
					data={section.data.flatMap((group) => group.items)}
					isLoading={section.isLoading}
				/>
			);
		case "products":
			return (
				<HomeApplet.Products
					data={section.data.flatMap((group) => group.items)}
					isLoading={section.isLoading}
				/>
			);
		case "topics":
			return (
				<HomeApplet.Topics
					data={section.data.flatMap((group) => group.items)}
					isLoading={section.isLoading}
				/>
			);
		case "itemTypes":
			return (
				<HomeApplet.ItemTypes
					data={section.data.flatMap((group) => group.items)}
					isLoading={section.isLoading}
				/>
			);
		default:
			return null;
	}
};

const HomeAppletBody = () => {
	const { sections, options } = useHomeApplet();

	return (
		<Box>
			<HomeApplet.Header />
			{sections.map((section, index) => (
				<Fragment key={`${section.type}-${index}`}>
					<SectionHeader
						appFont={options.appFont}
						title={section.header.title}
						chip={section.header.chip}
					/>
					<HomeAppletSectionItems section={section} />
				</Fragment>
			))}
		</Box>
	);
};

export const HomeApplet = Object.assign(HomeAppletProvider, {
	Header: HomeAppletHeader,
	Tasks: HomeAppletTasks,
	Products: HomeAppletProducts,
	Topics: HomeAppletTopics,
	ItemTypes: HomeAppletItemTypes,
	Body: HomeAppletBody,
});
